import { useState, useEffect, useCallback } from "react";

const SAVED_KEY = "driver_saved_loads";      // { [loadId]: loadSnapshot }
const AUTOSAVE_KEY = "driver_autosave_enabled";

function readSaved() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeSaved(map) {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable — fail silently */
  }
}

/**
 * Shared "heart" save state for loads, usable from the Load Board and the
 * Saved Loads page. Also supports an opt-in auto-save mode that quietly
 * hearts new Priority Match / Urgent loads as they come in, so a driver
 * doesn't have to babysit the board.
 */
export default function useSavedLoads() {
  const [savedMap, setSavedMap] = useState(readSaved);
  const [autoSave, setAutoSaveState] = useState(
    () => localStorage.getItem(AUTOSAVE_KEY) === "true"
  );

  // Keep multiple mounted components (LoadBoard + SavedLoads) in sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === SAVED_KEY) setSavedMap(readSaved());
      if (e.key === AUTOSAVE_KEY) setAutoSaveState(e.newValue === "true");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isSaved = useCallback((loadId) => !!savedMap[loadId], [savedMap]);

  const saveLoad = useCallback((load) => {
    setSavedMap((prev) => {
      const next = { ...prev, [load.pk ?? load.id]: { ...load, saved_at: new Date().toISOString() } };
      writeSaved(next);
      return next;
    });
  }, []);

  const unsaveLoad = useCallback((loadId) => {
    setSavedMap((prev) => {
      const next = { ...prev };
      delete next[loadId];
      writeSaved(next);
      return next;
    });
  }, []);

  const toggleSave = useCallback((load) => {
    const id = load.pk ?? load.id;
    if (savedMap[id]) unsaveLoad(id);
    else saveLoad(load);
  }, [savedMap, saveLoad, unsaveLoad]);

  const setAutoSave = useCallback((value) => {
    setAutoSaveState(value);
    localStorage.setItem(AUTOSAVE_KEY, value ? "true" : "false");
  }, []);

  // Call with the freshly fetched load list; when auto-save is on, quietly
  // hearts anything urgent, directly matched/invited, or — the real
  // qualification check added this round — anything the driver's own
  // profile (PPCA cert, insurance, state certification) actually qualifies
  // for. Pass `load.is_qualified_match` pre-computed by the caller (see
  // LoadBoardPage.jsx's getMatchInfo), since this hook doesn't have
  // access to the driver's profile data itself.
  const autoSaveIncoming = useCallback((loads, userId) => {
    if (!autoSave || !Array.isArray(loads)) return;
    setSavedMap((prev) => {
      let changed = false;
      const next = { ...prev };
      loads.forEach((load) => {
        const id = load.pk ?? load.id;
        const isMatched = load.invited_driver_ids?.includes(userId);
        const isUrgent = load.status === "Urgent";
        const isQualifiedMatch = !!load.is_qualified_match;
        if ((isMatched || isUrgent || isQualifiedMatch) && !next[id]) {
          next[id] = { ...load, saved_at: new Date().toISOString(), auto_saved: true };
          changed = true;
        }
      });
      if (changed) writeSaved(next);
      return changed ? next : prev;
    });
  }, [autoSave]);

  const savedLoads = Object.values(savedMap).sort(
    (a, b) => new Date(b.saved_at || 0) - new Date(a.saved_at || 0)
  );

  return { savedLoads, isSaved, toggleSave, autoSave, setAutoSave, autoSaveIncoming };
}
